import { useCallback, useState } from "react";

import { CanvasView, GRID_CONSTANTS, ViewDefinition } from "store/types";

const { GAP, GRID_CELL_HEIGHT, MARGIN, HEADER_HEIGHT } = GRID_CONSTANTS;

interface DraggingCard {
  id: string;
  startCol: number;
  startRow: number;
  offsetX: number;
  offsetY: number;
}

interface CardDragPreview {
  col: number;
  row: number;
  isValid: boolean;
}

interface UseCanvasDragDropProps {
  canvasRef: React.RefObject<HTMLDivElement>;
  currentPageCards: CanvasView[];
  gridCellWidth: number;
  draggedView: ViewDefinition | null;
  dragPreview: { col: number; row: number; span: number; isValid: boolean } | null;
  setDraggedView: (view: ViewDefinition | null) => void;
  setDragPreview: (preview: { col: number; row: number; span: number; isValid: boolean } | null) => void;
  snapToGrid: (x: number, y: number) => { col: number; row: number };
  isPositionOccupied: (col: number, row: number, span: number, excludeId?: string) => boolean;
  findAvailablePosition: (span: number, rowSpan?: number) => { col: number; row: number } | null;
  addCardToCanvas: (view: ViewDefinition, col: number, row: number, span: number) => void;
  updateCardPosition: (cardId: string, col: number, row: number) => void;
  setError: (error: string | null) => void;
}

interface UseCanvasDragDropReturn {
  draggingCard: DraggingCard | null;
  cardDragPreview: CardDragPreview | null;
  handleViewDragStart: (view: ViewDefinition) => (e: React.DragEvent) => void;
  handleViewDragEnd: () => void;
  handleCanvasDragOver: (e: React.DragEvent) => void;
  handleCanvasDrop: (e: React.DragEvent) => void;
  handleCanvasDragLeave: () => void;
  handleQuickAddCard: (view: ViewDefinition) => void;
  handleCardDragStart: (card: CanvasView, e: React.MouseEvent) => void;
  handleCardDragMove: (e: React.MouseEvent) => void;
  handleCardDragEnd: () => void;
}

export const useCanvasDragDrop = ({
  canvasRef,
  currentPageCards,
  gridCellWidth,
  draggedView,
  dragPreview,
  setDraggedView,
  setDragPreview,
  snapToGrid,
  isPositionOccupied,
  findAvailablePosition,
  addCardToCanvas,
  updateCardPosition,
  setError
}: UseCanvasDragDropProps): UseCanvasDragDropReturn => {
  // Drag-to-move state for cards
  const [draggingCard, setDraggingCard] = useState<DraggingCard | null>(null);
  const [cardDragPreview, setCardDragPreview] = useState<CardDragPreview | null>(null);

  // Drag handlers for views panel
  const handleViewDragStart = useCallback(
    (view: ViewDefinition) => (e: React.DragEvent) => {
      e.dataTransfer.effectAllowed = "copy";
      setDraggedView(view);
    },
    [setDraggedView]
  );

  const handleViewDragEnd = useCallback(() => {
    setDraggedView(null);
    setDragPreview(null);
  }, [setDraggedView, setDragPreview]);

  // Canvas drag handlers
  const handleCanvasDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = "copy";

      if (!canvasRef.current || !draggedView) return;

      const rect = canvasRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      const { col, row } = snapToGrid(x, y);
      const span = 6; // Default half-width

      const isValid = !isPositionOccupied(col, row, span);
      setDragPreview({ col, row, span, isValid });
    },
    [canvasRef, draggedView, snapToGrid, isPositionOccupied, setDragPreview]
  );

  const handleCanvasDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();

      if (!draggedView || !dragPreview || !dragPreview.isValid) {
        setDraggedView(null);
        setDragPreview(null);
        return;
      }

      addCardToCanvas(draggedView, dragPreview.col, dragPreview.row, dragPreview.span);
      setDraggedView(null);
      setDragPreview(null);
    },
    [draggedView, dragPreview, addCardToCanvas, setDraggedView, setDragPreview]
  );

  const handleCanvasDragLeave = useCallback(() => {
    setDragPreview(null);
  }, [setDragPreview]);

  // Quick add card (finds available position automatically)
  const handleQuickAddCard = useCallback(
    (view: ViewDefinition) => {
      const position = findAvailablePosition(6, view.type === "data_table" ? 4 : 2);
      if (position) {
        addCardToCanvas(view, position.col, position.row, 6);
      } else {
        setError("No space available on current page. Add a new page.");
      }
    },
    [findAvailablePosition, addCardToCanvas, setError]
  );

  // Card drag-to-move handlers
  const handleCardDragStart = useCallback(
    (card: CanvasView, e: React.MouseEvent) => {
      // Don't start drag if clicking on action buttons
      if ((e.target as HTMLElement).closest(".card-actions")) return;

      e.preventDefault();
      if (!canvasRef.current) return;

      const rect = canvasRef.current.getBoundingClientRect();
      const cardX = card.gridCol * (gridCellWidth + GAP);
      const cardY = card.gridRow * (GRID_CELL_HEIGHT + GAP);

      setDraggingCard({
        id: card.id,
        startCol: card.gridCol,
        startRow: card.gridRow,
        offsetX: e.clientX - rect.left - cardX,
        offsetY: e.clientY - rect.top - HEADER_HEIGHT - cardY
      });
      setCardDragPreview({
        col: card.gridCol,
        row: card.gridRow,
        isValid: true
      });
    },
    [canvasRef, gridCellWidth]
  );

  const handleCardDragMove = useCallback(
    (e: React.MouseEvent) => {
      if (!draggingCard || !canvasRef.current) return;

      const rect = canvasRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left - draggingCard.offsetX;
      const y = e.clientY - rect.top - HEADER_HEIGHT - draggingCard.offsetY;

      const { col, row } = snapToGrid(x + MARGIN, y + MARGIN + HEADER_HEIGHT);

      // Find the card to get its span
      const card = currentPageCards.find((c) => c.id === draggingCard.id);
      if (!card) return;

      const isValid = !isPositionOccupied(col, row, card.gridSpan, card.id);
      setCardDragPreview({ col, row, isValid });
    },
    [canvasRef, draggingCard, snapToGrid, currentPageCards, isPositionOccupied]
  );

  const handleCardDragEnd = useCallback(() => {
    if (draggingCard && cardDragPreview && cardDragPreview.isValid) {
      updateCardPosition(draggingCard.id, cardDragPreview.col, cardDragPreview.row);
    }
    setDraggingCard(null);
    setCardDragPreview(null);
  }, [draggingCard, cardDragPreview, updateCardPosition]);

  return {
    draggingCard,
    cardDragPreview,
    handleViewDragStart,
    handleViewDragEnd,
    handleCanvasDragOver,
    handleCanvasDrop,
    handleCanvasDragLeave,
    handleQuickAddCard,
    handleCardDragStart,
    handleCardDragMove,
    handleCardDragEnd
  };
};
